<div class="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
  <!-- Header -->
  <div class="mb-8">
    <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h1 class="mb-2 text-3xl font-bold text-gray-900">Machine reservations</h1>
        <p class="text-gray-600">Book machines by date range with conflict detection</p>
      </div>
      <div class="flex flex-wrap items-center gap-3">
        <div class="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
              <mat-icon class="text-blue-600 !text-xl !h-5 !w-5">event_available</mat-icon>
            </div>
            <div>
              <p class="text-sm text-gray-600">Total reservations</p>
              <p class="text-lg font-semibold text-gray-900">{{ reservations.length }}</p>
            </div>
          </div>
        </div>
        <div class="view-toggle">
          <button
            type="button"
            class="view-toggle-btn"
            [class.view-toggle-btn--active]="viewMode === 'list'"
            (click)="setView('list')"
          >
            List
          </button>
          <button
            type="button"
            class="view-toggle-btn"
            [class.view-toggle-btn--active]="viewMode === 'calendar'"
            (click)="setView('calendar')"
          >
            Calendar
          </button>
        </div>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-xl border border-transparent bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-lg transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          (click)="openBookDialog()"
        >
          <mat-icon class="mr-1 !text-lg">event_available</mat-icon>
          Book machine
        </button>
      </div>
    </div>
  </div>

  <!-- Filters -->
  <div class="mb-8 rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6">
      <div class="lg:col-span-1">
        <label class="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500">Machine</label>
        <select
          class="filter-control"
          [(ngModel)]="filters.machine_id"
          (change)="refresh()"
        >
          <option value="">All machines</option>
          <option *ngFor="let m of machines" [value]="m.id">{{ m.machine_name }} ({{ m.machine_id }})</option>
        </select>
      </div>

      <div class="lg:col-span-1" *ngIf="viewMode === 'list'">
        <label class="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500">Status</label>
        <select class="filter-control" [(ngModel)]="filters.status" (change)="load()">
          <option *ngFor="let s of statusOptions" [value]="s.value">{{ s.label }}</option>
        </select>
      </div>

      <div class="lg:col-span-1" *ngIf="viewMode === 'list'">
        <label class="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500">From</label>
        <input
          type="date"
          class="filter-control"
          [(ngModel)]="filters.from_date"
          (change)="load()"
        />
      </div>

      <div class="lg:col-span-1" *ngIf="viewMode === 'list'">
        <label class="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500">To</label>
        <input
          type="date"
          class="filter-control"
          [(ngModel)]="filters.to_date"
          (change)="load()"
        />
      </div>

      <div class="lg:col-span-2" *ngIf="viewMode === 'list'">
        <label class="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-500">Search</label>
        <div class="relative">
          <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <mat-icon class="!text-lg text-gray-400">search</mat-icon>
          </div>
          <input
            type="text"
            class="filter-control !pl-10"
            [(ngModel)]="filters.search"
            (keyup.enter)="load()"
            placeholder="Title or machine name…"
          />
        </div>
      </div>

      <div class="flex items-end gap-2" [class.lg:col-span-1]="viewMode === 'calendar'" [class.lg:col-span-6]="viewMode === 'list'">
        <button
          type="button"
          class="inline-flex flex-1 items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 lg:flex-none"
          (click)="refresh()"
        >
          Refresh
        </button>
        <button
          type="button"
          *ngIf="viewMode === 'list'"
          class="inline-flex flex-1 items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 lg:flex-none"
          (click)="clearFilters()"
        >
          Clear
        </button>
      </div>
    </div>
  </div>

  <div *ngIf="loading" class="rounded-2xl border border-gray-200 bg-white py-16 text-center text-gray-500 shadow-lg">
    Loading reservations…
  </div>

  <!-- List view -->
  <div *ngIf="!loading && viewMode === 'list'" class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
    <div class="border-b border-gray-200 bg-gray-50 px-6 py-4">
      <h2 class="text-lg font-semibold text-gray-900">Reservation overview</h2>
      <p class="mt-1 text-sm text-gray-600">Manage machine bookings and availability</p>
    </div>

    <div class="overflow-x-auto">
      <table class="min-w-full text-sm">
        <thead class="bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
          <tr>
            <th class="px-6 py-4">Machine</th>
            <th class="px-6 py-4">Title</th>
            <th class="px-6 py-4">Project</th>
            <th class="px-6 py-4">Dates</th>
            <th class="px-6 py-4">Operator</th>
            <th class="px-6 py-4">Status</th>
            <th class="px-6 py-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let r of reservations" class="border-t border-gray-100 transition-colors hover:bg-gray-50">
            <td class="px-6 py-4">
              <div class="font-medium text-gray-900">{{ r.machine_name }}</div>
              <div class="text-xs text-gray-500">{{ r.machine_code }}</div>
            </td>
            <td class="px-6 py-4 text-gray-900">{{ r.title }}</td>
            <td class="px-6 py-4 text-gray-900">{{ r.project_name || '—' }}</td>
            <td class="whitespace-nowrap px-6 py-4 text-gray-900">
              {{ r.start_date | date:'mediumDate' }} – {{ r.end_date | date:'mediumDate' }}
            </td>
            <td class="px-6 py-4 text-gray-900">{{ r.operator_name || '—' }}</td>
            <td class="px-6 py-4">
              <span class="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize" [ngClass]="statusClass(r.status)">
                {{ r.status.replace('_', ' ') }}
              </span>
            </td>
            <td class="whitespace-nowrap px-6 py-4 text-right">
              <button
                mat-icon-button
                matTooltip="Edit reservation"
                class="!text-gray-600 hover:!text-blue-600"
                (click)="openBookDialog(r)"
              >
                <mat-icon>edit</mat-icon>
              </button>
              <button
                *ngIf="canCancel(r)"
                mat-icon-button
                matTooltip="Cancel reservation"
                color="warn"
                (click)="cancelReservation(r)"
              >
                <mat-icon>event_busy</mat-icon>
              </button>
            </td>
          </tr>
          <tr *ngIf="reservations.length === 0">
            <td colspan="7" class="px-6 py-12 text-center">
              <div class="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                <mat-icon class="text-gray-400 !text-3xl">event_busy</mat-icon>
              </div>
              <p class="text-base font-medium text-gray-900">No reservations found</p>
              <p class="mt-1 text-sm text-gray-500">Try adjusting filters or book a machine to get started.</p>
              <button
                type="button"
                class="mt-4 inline-flex items-center rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
                (click)="openBookDialog()"
              >
                Book machine
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Calendar view -->
  <div *ngIf="!loading && viewMode === 'calendar'" class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
    <div
      *ngIf="reservations.length === 0"
      class="border-b border-gray-200 bg-gray-50 px-6 py-8 text-center"
    >
      <div class="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
        <mat-icon class="text-gray-400 !text-2xl">event_busy</mat-icon>
      </div>
      <p class="text-base font-medium text-gray-900">No reservations found</p>
      <p class="mt-1 text-sm text-gray-500">No machine bookings for this period. Book a machine to see it on the calendar.</p>
      <button
        type="button"
        class="mt-4 inline-flex items-center rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
        (click)="openBookDialog()"
      >
        Book machine
      </button>
    </div>
    <div class="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
      <button mat-icon-button type="button" (click)="prevMonth()" aria-label="Previous month">
        <mat-icon>chevron_left</mat-icon>
      </button>
      <h2 class="text-lg font-semibold text-gray-900">{{ calendarTitle }}</h2>
      <button mat-icon-button type="button" (click)="nextMonth()" aria-label="Next month">
        <mat-icon>chevron_right</mat-icon>
      </button>
    </div>

    <div class="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
      <div *ngFor="let d of weekDays" class="px-2 py-2 text-center text-xs font-semibold uppercase text-gray-500">
        {{ d }}
      </div>
    </div>

    <div class="grid min-h-[520px] auto-rows-fr grid-cols-7">
      <div
        *ngFor="let day of calendarDays"
        class="min-h-[88px] border-b border-r border-gray-100 p-1.5 align-top"
        [class.bg-gray-50]="!day.inMonth"
        [class.ring-2]="day.isToday"
        [class.ring-inset]="day.isToday"
        [class.ring-blue-400]="day.isToday"
      >
        <div class="mb-1 text-xs font-medium" [class.text-gray-400]="!day.inMonth">{{ day.date.getDate() }}</div>
        <div class="space-y-1">
          <button
            type="button"
            *ngFor="let r of day.reservations | slice:0:3"
            class="w-full truncate rounded px-1.5 py-0.5 text-left text-[10px] leading-tight text-white"
            [ngClass]="reservationBarClass(r.status)"
            (click)="openBookDialog(r)"
            [title]="r.title + ' — ' + r.machine_name"
          >
            {{ r.machine_name }}: {{ r.title }}
          </button>
          <div *ngIf="day.reservations.length > 3" class="px-1 text-[10px] text-gray-500">
            +{{ day.reservations.length - 3 }} more
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
